import type { ReactNode } from "react"
import {
	IconInboxOutline18 as InboxIcon,
	IconPlusOutline18 as PlusIcon,
} from "nucleo-ui-outline-18"

import { Button } from "@productlane/ui"

import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"

const TITLE = "Everything is assigned"
const TEXT = "New threads arrive here first."

const TITLE_CLASS = "text-[15px] font-semibold text-primary"
const TEXT_CLASS = "text-sm text-secondary"

// 1. Bare large icon, hairline stroke, no container.
function BareIcon() {
	return (
		<div className="flex flex-col items-center gap-4 text-center">
			<InboxIcon
				className="h-14 w-14 text-gray-400 dark:text-gray-500"
				strokeWidth={0.6}
			/>
			<div className="flex flex-col gap-1">
				<div className={TITLE_CLASS}>{TITLE}</div>
				<div className={TEXT_CLASS}>{TEXT}</div>
			</div>
		</div>
	)
}

// 2. Icon in a soft tile with a translucent ring, like a shadowed surface.
function TileIcon() {
	return (
		<div className="flex flex-col items-center gap-5 text-center">
			<div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
				<InboxIcon
					className="h-7 w-7 text-gray-600 dark:text-gray-300"
					strokeWidth={1.1}
				/>
			</div>
			<div className="flex flex-col gap-1">
				<div className={TITLE_CLASS}>{TITLE}</div>
				<div className={TEXT_CLASS}>{TEXT}</div>
			</div>
		</div>
	)
}

// 3. Icon centered on concentric rings that fade outward.
function RingsIcon() {
	return (
		<div className="flex flex-col items-center gap-5 text-center">
			<div className="relative flex h-28 w-28 items-center justify-center">
				<div className="absolute inset-0 rounded-full ring-1 ring-black/[0.04] dark:ring-white/[0.05]" />
				<div className="absolute inset-3.5 rounded-full ring-1 ring-black/[0.07] dark:ring-white/[0.08]" />
				<div className="absolute inset-7 rounded-full bg-white ring-1 ring-black/10 dark:bg-gray-850 dark:ring-white/10" />
				<InboxIcon
					className="relative h-6 w-6 text-gray-600 dark:text-gray-300"
					strokeWidth={1.1}
				/>
			</div>
			<div className="flex flex-col gap-1">
				<div className={TITLE_CLASS}>{TITLE}</div>
				<div className={TEXT_CLASS}>{TEXT}</div>
			</div>
		</div>
	)
}

// 4. Large icon fading into the page, with one action underneath.
function FadedIconWithAction() {
	return (
		<div className="flex flex-col items-center gap-5 text-center">
			<InboxIcon
				className="h-20 w-20 text-gray-300 [mask-image:linear-gradient(to_bottom,black_35%,transparent)] dark:text-gray-600"
				strokeWidth={0.5}
			/>
			<div className="flex flex-col gap-1">
				<div className={TITLE_CLASS}>{TITLE}</div>
				<div className={TEXT_CLASS}>{TEXT}</div>
			</div>
			<Button variant="outline" Icon={PlusIcon}>
				New thread
			</Button>
		</div>
	)
}

// 5. Left-aligned: small tile and text read as a note, not a poster.
function LeftAligned() {
	return (
		<div className="flex max-w-[260px] flex-col items-start gap-4 text-left">
			<div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gray-100 dark:bg-gray-800">
				<InboxIcon
					className="h-5 w-5 text-gray-600 dark:text-gray-300"
					strokeWidth={1.3}
				/>
			</div>
			<div className="flex flex-col gap-1">
				<div className={TITLE_CLASS}>{TITLE}</div>
				<div className={TEXT_CLASS}>{TEXT}</div>
			</div>
		</div>
	)
}

const VARIANTS: { name: string; note: string; node: ReactNode }[] = [
	{
		name: "1. Bare",
		note: "56px hairline icon, no container",
		node: <BareIcon />,
	},
	{ name: "2. Tile", note: "Icon in a ringed tile", node: <TileIcon /> },
	{
		name: "3. Rings",
		note: "Icon on fading concentric rings",
		node: <RingsIcon />,
	},
	{
		name: "4. Faded + action",
		note: "80px icon fading out, one button",
		node: <FadedIconWithAction />,
	},
	{
		name: "5. Left aligned",
		note: "Compact tile, text on the left",
		node: <LeftAligned />,
	},
]

export function EmptyStatePage() {
	if (!isDevEnvironment) return <NotFound />

	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1280px] px-6 py-10 pb-24">
				<div className="mb-8">
					<div className="text-lg font-semibold text-primary">
						Empty state, five options
					</div>
					<div className="text-sm text-secondary">
						Shown in an inbox list column, 360px wide.
					</div>
				</div>
				<div className="flex flex-wrap gap-6">
					{VARIANTS.map((v) => (
						<div key={v.name} className="flex flex-col gap-2">
							<div>
								<div className="text-sm font-medium text-primary">{v.name}</div>
								<div className="text-xs text-secondary">{v.note}</div>
							</div>
							<div className="flex h-[440px] w-[360px] flex-col overflow-hidden rounded-lg border border-gray-200 bg-white dark:border-gray-700/80 dark:bg-gray-900">
								<div className="flex h-12 shrink-0 items-center border-b border-gray-200 px-4 text-sm font-medium text-primary dark:border-gray-700/80">
									Open
								</div>
								<div className="flex flex-1 items-center justify-center px-6">
									{v.node}
								</div>
							</div>
						</div>
					))}
				</div>
			</div>
		</div>
	)
}
